import { useMemo } from "react";
import { Show } from "shared/components";
import { IncomeSourceStatus, Money, VerifyMethodType } from "shared/types";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { ExpandableTable } from "../../../components/UniversalTable";
import { AuthenticityScoreAlert } from "../../../components/AuthenticityScoreAlert";
import { DocumentIssueAlert } from "../../../components/DocumentIssueAlert";
import { form1099TableColumns } from "../../../helpers";
import { useCombinedIncomeContext } from "../../../providers/useCombinedIncome";
import { UploadedIncomeSource } from "../../../types/income";
import { NameMismatch } from "../../../types/verified/common";
import { Form1099Fields, Form1099Item } from "../../../types/verified/form1099";
import { resolveAuthenticityTone } from "../../../utils/authenticityTone";
import {
  NAME_MISMATCH_LABEL,
  resolveDocumentProblems,
  toFraudRiskLines
} from "../../../utils/documentProblems";
import {
  resolveAuthenticityScore,
  resolveDocumentUrl,
  resolveFraudRisk,
  resolveRiskSummary
} from "../../../utils/fraudRisk";
import { rejectionReasonsToIssues } from "../utils";
import { NameMismatchAlert } from "./NameMismatchAlert";
import { buildFailedW2Row, mapW2FormsWithSourceData } from "./W2Table/utils";

type Form1099Row = ReturnType<typeof mapW2FormsWithSourceData>[number] & {
  form_type?: string;
  rejection_reason?: string[];
  name_mismatch?: NameMismatch | null;
};

// Usually an SSN.
const maskTin = (value?: Form1099Fields["recipient_tin"]) => {
  const digits = String(value?.value ?? "").replace(/\D/g, "");
  return digits.length >= 4 ? `***-**-${digits.slice(-4)}` : "-";
};

const fieldValue = (
  fields: Form1099Fields | undefined,
  key: keyof Form1099Fields
): string | number | Money => fields?.[key]?.value || "-";

export const Form1099Table = ({
  incomeItems,
  failedIncomeSources = []
}: {
  incomeItems?: Form1099Item[];
  failedIncomeSources?: UploadedIncomeSource[];
}) => {
  const { incomeSourceTrustScoresMap, incomeSourceRejectionReasonsMap } =
    useCombinedIncomeContext();

  const rows = useMemo(() => {
    const itemRows =
      incomeItems?.flatMap((item) =>
        mapW2FormsWithSourceData(item, VerifyMethodType.Form1099)
      ) ?? [];
    const itemSourceIds = new Set(itemRows.map((row) => row.income_source_id));
    const failedSources = failedIncomeSources.filter(
      (source) => source.status === IncomeSourceStatus.Failed
    );
    // Items carry no status, and a source can fail after its item was saved.
    const failedSourceIds = new Set(failedSources.map((source) => source.id));
    const failedRows = failedSources
      .filter((source) => !itemSourceIds.has(source.id))
      .map((source) => buildFailedW2Row(source, VerifyMethodType.Form1099));

    return [...itemRows, ...failedRows].map((row): Form1099Row => {
      const sourceId = row.income_source_id;
      const sourceRejectionReason =
        sourceId !== undefined
          ? incomeSourceRejectionReasonsMap[sourceId]
          : undefined;
      return {
        ...row,
        ...(sourceId !== undefined &&
          failedSourceIds.has(sourceId) && {
            source_status: IncomeSourceStatus.Failed
          }),
        trust_score:
          row.trust_score ??
          (sourceId !== undefined
            ? incomeSourceTrustScoresMap[sourceId]
            : undefined),
        rejection_reason: sourceRejectionReason?.length
          ? sourceRejectionReason
          : (row as Form1099Row).rejection_reason
      };
    });
  }, [
    incomeItems,
    failedIncomeSources,
    incomeSourceTrustScoresMap,
    incomeSourceRejectionReasonsMap
  ]);

  return (
    <ExpandableTable
      cellClassName="p-4 pl-0 text-sm self-center"
      headerRowClassName="text-content-300 font-medium"
      renderExpanded={(form: Form1099Row) => {
        const fields = (form as { fields?: Form1099Fields }).fields;
        const isMisc = form.form_type === "1099-MISC";
        const problems = resolveDocumentProblems(form);
        const score =
          resolveAuthenticityScore(form) ?? form.trust_score ?? null;
        const issues = toFraudRiskLines(
          rejectionReasonsToIssues(
            form.rejection_reason,
            VerifyMethodType.Form1099
          ),
          resolveRiskSummary(form)
        );

        return (
          <div className="p-6 bg-background-900">
            <div className="space-y-3 mb-4">
              <Show if={Boolean(form.name_mismatch)}>
                <NameMismatchAlert nameMismatch={form.name_mismatch!} />
              </Show>

              <Show if={problems.length > 1}>
                {problems
                  .filter((problem) => problem.label !== NAME_MISMATCH_LABEL)
                  .map((problem) => (
                    <DocumentIssueAlert
                      key={problem.label}
                      label={problem.label}
                      description={problem.description}
                    />
                  ))}
              </Show>

              <AuthenticityScoreAlert
                tone={resolveAuthenticityTone(resolveFraudRisk(form), score)}
                score={score}
                issues={issues}
              />
            </div>

            <ContentTitle
              title="Income and tax information"
              contentClassName="space-y-2"
            >
              <KeyValue label="Year" value={form.year || "-"} />
              <Show if={!isMisc}>
                <KeyValue
                  label="Nonemployee compensation"
                  value={fieldValue(fields, "non_employee_compensation")}
                />
              </Show>
              <Show if={isMisc}>
                <KeyValue label="Rents" value={fieldValue(fields, "rents")} />
                <KeyValue
                  label="Royalties"
                  value={fieldValue(fields, "royalties")}
                />
                <KeyValue
                  label="Other income"
                  value={fieldValue(fields, "other_income")}
                />
                <KeyValue
                  label="Medical and health care payments"
                  value={fieldValue(fields, "medical_and_healthcare")}
                />
                <KeyValue
                  label="Gross proceeds paid to an attorney"
                  value={fieldValue(fields, "gross_attorney_payments")}
                />
              </Show>
              <KeyValue
                label="Federal income tax withheld"
                value={fieldValue(fields, "federal_income_tax_withheld")}
              />
              <KeyValue
                label="State tax withheld"
                value={fieldValue(fields, "state_tax_withheld")}
              />
              <KeyValue
                label="State income"
                value={fieldValue(fields, "state_income")}
              />
            </ContentTitle>

            <ContentTitle
              title="Payer information"
              contentClassName="space-y-2"
            >
              <KeyValue
                label="Payer name"
                value={fieldValue(fields, "payer_name")}
              />
              <KeyValue
                label="Payer TIN"
                value={fieldValue(fields, "payer_tin")}
              />
              <KeyValue
                label="Payer address"
                value={fieldValue(fields, "payer_address")}
              />
            </ContentTitle>

            <ContentTitle
              title="Recipient information"
              contentClassName="space-y-2"
            >
              <KeyValue
                label="Recipient name"
                value={fieldValue(fields, "recipient_name")}
              />
              <KeyValue
                label="Recipient TIN"
                value={maskTin(fields?.recipient_tin)}
              />
              <KeyValue
                label="Recipient address"
                value={fieldValue(fields, "recipient_address")}
              />
            </ContentTitle>
          </div>
        );
      }}
      rows={rows}
      columns={form1099TableColumns}
      getViewUrl={resolveDocumentUrl}
    />
  );
};
